(function (Lfl, $, undefined) {
    /**
     * @memberOf Lfl
     */
    Lfl.VisibilityDependence = Lfl.VisibilityDependence || {};

    (function (VisibilityDependence, $, undefined) {
        if (Lfl.Files.isLoadedAlready(Lfl.VisibilityDependence, 'DatatableVdoiCoordinator')) {
            return;
        }

        /*
         |===============================================================================================================
         | Class definition
         |===============================================================================================================
         */

        /**
         * Sets up and coordinates when datatable's elements' visibility depends on input.
         *
         * E.g., when some table's rows or columns are to be hidden/shown depending on whether some checkbox is checked.
         *
         * @param {object} parameters
         * @param {Lfl.VisibilityDependence.VdoiControllingInputResolver} parameters.controllingInput The resolved
         * controlling input details.
         * @param {string} parameters.tablePath
         * @param {string} parameters.dependentTableElementsPath The path to the elements which should be hidden when
         * the value (or any of the values) is not selected. In this case, it will be the table's rows and/or columns.
         * Note: This class only handles contents of the table (link: Vdoi::Datatables::OnlyRowsAndCols).
         * @param {boolean} [parameters.autoActivate=false] Whether the mechanisms should be activated immediately, or wait
         * until the client code activates them manually. This mechanism is primarily intended an API for PHP builders
         * which can use a variable to either immediately print and run the mechanisms, or allow the client JS to build
         * the object further.
         *
         * Note: No class parameters (selectedClass and notSelectedClass), because we have to use datatable's api to
         *          achieve this. See: DataTables::FitOnSmallDisplays::ColumnResizing::UsingDatatable, especially
         *          DataTables::HiddenPagesAreNotInDom
         *      Neither do we have the option to focus the controlling or controlled element - does not make much sense
         *          for the datatables scenario. (Although focusTheControllingInputFirstTime might make sense)
         * Note: Coordinator name suffix is based on a discussing about naming (link: Vdoi::CoordinatorNaming):
         *          https://stackoverflow.com/questions/1866794/naming-classes-how-to-avoid-calling-everything-a-whatevermanager
         * @memberOf Lfl.VisibilityDependence
         * @lends Lfl.VisibilityDependence.DatatableVdoiCoordinator
         * @constructor
         */
        Lfl.VisibilityDependence.DatatableVdoiCoordinator = function (parameters) {
            /*
             |-----------------------------------------------------------------------------------------------------------
             | Construction
             |-----------------------------------------------------------------------------------------------------------
             */

            var dvc = this;
            var tablePath;
            var $table;
            /**
             * @type {DataTable}
             */
            var datatable;

            /**
             * @type {Lfl.VisibilityDependence.VdoiControllingInputResolver}
             */
            var controllingInput;
            /**
             * @type {Lfl.VisibilityDependence.VdoiEventDispatcher}
             */
            var eventDispatcher;

            var $dependentTableElements;
            var columnsToHide;
            var rowsToHide;
            var rowIndicesToHide;

            function init() {
                // Auto-activation
                if (parameters.autoActivate === true) {
                    dvc.activate();
                }

                // Note: most variables are initialized only after activation.
            }

            /*
             |-----------------------------------------------------------------------------------------------------------
             | Public methods
             |-----------------------------------------------------------------------------------------------------------
             */

            /**
             * Activates the visibility dependence.
             */
            this.activate = function () {
                initVariables();
                setupColumnsAndRowsHiding();
            };

            // Note: Builder methods for JS client code are expected to be added later.

            /*
             |-----------------------------------------------------------------------------------------------------------
             | Private methods
             |-----------------------------------------------------------------------------------------------------------
             */

            function initVariables() {
                resolveTableAndDatatableApi();

                controllingInput = parameters.controllingInput;
                $dependentTableElements = $(parameters.dependentTableElementsPath);

                /*
                |-------------------------------------------------------------------------
                | Dependent table elements
                | Note: We only select elements accessible via columns/rows methods.
                |       Link: Vdoi::Datatables::OnlyRowsAndCols */
                columnsToHide = datatable.columns(parameters.dependentTableElementsPath);
                rowsToHide = datatable.rows(parameters.dependentTableElementsPath);
                rowIndicesToHide = [];
                rowsToHide.every(function (index) {
                    rowIndicesToHide.push(index);
                });
            }

            function resolveTableAndDatatableApi() {
                tablePath = parameters.tablePath;
                var tableResolver = new Lfl.Tables.DatatableResolver(tablePath);
                datatable = tableResolver.getApi();
            }

            /*
            |-------------------------------------------------------------------------
            | Rows and columns visibility */
            function setupColumnsAndRowsHiding() {
                /*
                |-------------------------------------------------------------------------
                | Event handling and columns */
                eventDispatcher = new Lfl.VisibilityDependence.VdoiEventDispatcher({controllingInput: controllingInput});

                eventDispatcher.onShowing(handleShowing);
                eventDispatcher.onHiding(handleHiding);

                /*
                |-------------------------------------------------------------------------
                | Rows */
                // Note: Rows do not have a visible() function, so we use "search" filtering instead
                //       Link: DataTables::FitOnSmallDisplays::ColumnResizing::UsingDatatable::FilteringRows
                $.fn.dataTable.ext.search.push(
                    function (settings, searchData, index, rowData, counter) {
                        if ($(settings.nTable).is(tablePath) &&  // Only applies for the desired table
                            rowIndicesToHide.includes(index)) { // Only applies for the desired rows
                            return eventDispatcher.isVisible();
                        }

                        return true;
                    }
                );

                /*
                |-------------------------------------------------------------------------
                | Clear initial */
                removeInitialVdoiClasses(columnsToHide, rowsToHide);
                Lfl.Tables.DatatablesWidthRefresher.refreshWidth(tablePath);
            }

            function handleShowing() {
                handleChange(true);
            }

            function handleHiding() {
                handleChange(false);
            }

            function handleChange(becomingVisible) {
                columnsToHide.every(function (index) {
                    this.visible(becomingVisible);
                    // Note: Columns are still searchable (e.g., searching for "ne" will still show all
                    //       rows which contain that word in their hidden column).
                    //       And this can not be easily resolved -
                    //         Link: DataTables::FitOnSmallDisplays::ColumnResizing::HiddenAreSearchable
                });

                Lfl.Tables.DatatablesWidthRefresher.refreshWidth(tablePath);
            }

            function removeInitialVdoiClasses(cols, rows) {
                var classesToRemove = 'vdoi-datatable-initially-hidden';
                // Link: DataTables::HiddenPagesAreNotInDom::ButCanBeAccessed
                cols.every(function () {
                    // Remove from <td> elements
                    $(this.nodes()).removeClass(classesToRemove);
                    // Remove from both <th> elements
                    $(this.header()).removeClass(classesToRemove);
                    $(this.footer()).removeClass(classesToRemove);
                });

                rows.every(function () {
                    // Remove from <tr> element
                    $(this.nodes()).removeClass(classesToRemove);
                });
            }

            /*
             |-------------------------------------------------------------------------------------------------------
             | First initialization
             |-------------------------------------------------------------------------------------------------------
             */
            init();
        };
    }(Lfl.VisibilityDependence, jQuery));
}(window.Lfl = window.Lfl || {}, jQuery));
